/**
 * Sets the 'preferred_language' cookie to persist user choice.
 * Matches the cookie name used by the PHP CookieDetector.
 *
 * @param code The ISO language code (e.g. 'tr', 'en')
 */
const setLanguageCookie = (code: string): void => {
  const oneYear = 60 * 60 * 24 * 365;
  // Use Lax for better compatibility while maintaining security
  document.cookie = `preferred_language=${code}; path=/; max-age=${oneYear}; samesite=lax`;
};

/**
 * Initializes the language switcher interactions.
 * Handles both the simple links and the dropdown UI state.
 */
export const initLanguageSwitcher = (): void => {
  // --- 1. Selection Logic (Cookie + Reload) ---
  // Target any link with data-lang-code anywhere in the header/mobile menu
  const langLinks =
    document.querySelectorAll<HTMLAnchorElement>("[data-lang-code]");

  langLinks.forEach((link) => {
    link.addEventListener("click", (e) => {
      e.preventDefault();

      const targetLang = link.dataset.langCode;

      if (targetLang) {
        // Update the cookie
        setLanguageCookie(targetLang);

        // Reload the current page. The browser will send the new cookie
        // in the request headers, and the PHP backend will render
        // the site in the new language.
        window.location.reload();
      }
    });
  });

  // --- 2. UI Toggle Logic (For Dropdown styles only) ---
  const switchers = document.querySelectorAll<HTMLElement>(
    "[data-lang-switcher]",
  );

  // If there are no dropdowns (like in the Aventra header), stop here.
  if (switchers.length === 0) return;

  switchers.forEach((switcher) => {
    const trigger = switcher.querySelector<HTMLButtonElement>(
      ".lang-switcher__trigger",
    );
    const dropdown = switcher.querySelector<HTMLElement>(
      ".lang-switcher__dropdown",
    );

    if (!trigger || !dropdown) return;

    const toggleSwitcher = (state?: boolean) => {
      const isOpening = state ?? !switcher.classList.contains("is-open");
      switcher.classList.toggle("is-open", isOpening);
      trigger.setAttribute("aria-expanded", String(isOpening));
      dropdown.setAttribute("aria-hidden", String(!isOpening));
    };

    trigger.addEventListener("click", (e) => {
      e.stopPropagation();
      toggleSwitcher();
    });

    // Close when clicking outside
    document.addEventListener("click", (e) => {
      if (!switcher.contains(e.target as Node)) {
        toggleSwitcher(false);
      }
    });

    // Close on Escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && switcher.classList.contains("is-open")) {
        toggleSwitcher(false);
        trigger.focus();
      }
    });
  });
};
